/* eslint import/no-extraneous-dependencies: off */
import { createSlice } from '@reduxjs/toolkit';
import history from '@history';
import _ from '@lodash';
import AccessManagementService from 'app/admin/user/service/AccessManagementService';
import { setInitialSettings, setDefaultSettings } from 'app/store/fuse/settingsSlice';
import { changeLanguage } from 'app/store/i18nSlice';

const ALL_SERVICES = [
	// Operator
	"WHATIF",
	"UNIT_OPTIMIZER_OVERVIEW",
	"OPTIMIZATION_PIPELINE",

	// Expert
	"OPTIMIZATIONCASE",
	"OPTIMIZER",
	"OPTIMIZERLOG",
	"DATASET",
	"ASPECT",
	"WHATIFEXPERT",
	"WHATIFGROUP",
	"FITTINGCASE",
	"TASKMANAGER",
	"FITTERLOG",
	"TRANSFORMER",
	"MEASUREMENT",
	"SCHEDULER",
	"OPTIMIZATION_PIPELINE_LOG",
	"SIMULATION_MONITOR",
	"SIMULATION_MONITOR_LOG",
	"EDT_DYNAMIC_CHECK",
	"EDT_DYNAMIC_CHECK_LOG",
	"PI_WRITER",
	"PI_WRITER_LOG",
	"OPC_DA_WRITER",
	"OPC_DA_WRITER_LOG",
	"UNIT_OPTIMIZER",
	"UNIT_OPTIMIZER_LOG",
	"MODEL_VALIDATION",
	"MODEL_VALIDATION_LOG",
	"OUTLIER_DETECTOR",

	// Tech
	"MODEL",
	"DRIVER",
	"DRIVERLOG",
	"PI_MEASUREMENT_READER",
	"PI_AF_EVENT_READER",
	"PI_CONNECTION",
	"LAB_MEASUREMENT_READER",
	"MEASUREMENTUNIT",
	"PICTOGRAM",

	// User / notification
	"USERPROFILE",
	"USERACCOUNT",
	"USERROLE",
	"USERGROUP",
	"NOTIFICATIONS"
];

const ROLE_SERVICE_MAP = {
	operator: [
		"WHATIF",
		"UNIT_OPTIMIZER_OVERVIEW",
		"OPTIMIZATION_PIPELINE",
		"USERPROFILE",
		"NOTIFICATIONS"
	],
	expert: [
		// Operator
		"WHATIF",
		"UNIT_OPTIMIZER_OVERVIEW",
		"OPTIMIZATION_PIPELINE",
		"USERPROFILE",

		// Expert
		"OPTIMIZATIONCASE",
		"OPTIMIZER",
		"OPTIMIZERLOG",
		"DATASET",
		"ASPECT",
		"WHATIFEXPERT",
		"WHATIFGROUP",
		"FITTINGCASE",
		"TASKMANAGER",
		"FITTERLOG",
		"TRANSFORMER",
		"MEASUREMENT",
		"SCHEDULER",
		"OPTIMIZATION_PIPELINE_LOG",
		"SIMULATION_MONITOR",
		"SIMULATION_MONITOR_LOG",
		"EDT_DYNAMIC_CHECK",
		"EDT_DYNAMIC_CHECK_LOG",
		"PI_WRITER",
		"PI_WRITER_LOG",
		"OPC_DA_WRITER",
		"OPC_DA_WRITER_LOG",
		"UNIT_OPTIMIZER",
		"UNIT_OPTIMIZER_LOG",
		"MODEL_VALIDATION",
		"MODEL_VALIDATION_LOG",
		"OUTLIER_DETECTOR",

		// Tech
		"MODEL",
		"DRIVER",
		"DRIVERLOG",
		"PI_MEASUREMENT_READER",
		"PI_AF_EVENT_READER",
		"PI_CONNECTION",
		"LAB_MEASUREMENT_READER",
		"MEASUREMENTUNIT",
		"PICTOGRAM",

		// Notifications (mivel “minden blokk kivéve user”)
		"NOTIFICATIONS"
	],
	admin: [
		...ALL_SERVICES
	]
};

const normalizeRole = (v) => (v == null ? null : String(v).toLowerCase());

const safeArr = (v) => (Array.isArray(v) ? v : []);

const extractClientFromState = (stateDto) => {
	if (!stateDto || typeof stateDto !== 'object') return null;
	if (stateDto.client && typeof stateDto.client === 'object') return stateDto.client;
	// fallback: some backends returned the client directly
	if (stateDto.id != null && (stateDto.email != null || stateDto.name != null)) return stateDto;
	return null;
};

const convertClientToAccessConfig = ({ client, userGroups = [], roles = [] }, accessRole) => {
	const roleKey = normalizeRole(accessRole);
	// backend role names can differ from our internal permission buckets (pl. "user" -> operator)
	const permRoleKey = roleKey === 'user' ? 'operator' : roleKey;
	const serviceList = ROLE_SERVICE_MAP[permRoleKey] ? ROLE_SERVICE_MAP[permRoleKey] : (ROLE_SERVICE_MAP.operator || []);
	const serviceLevelMap = serviceList.reduce((acc, s) => ({ ...acc, [s]: "WriteRead" }), {});
	return {
		user: {
			id: client?.id ?? null,
			name: client?.name ?? null,
			email: client?.email ?? null,
			profilePicture: client?.profilePicture ?? null,
			currentLocale: "en",
			whatIfGroupList: [],
			userGroups: safeArr(userGroups),
			roles: safeArr(roles)
		},
		roleList: [{ servicePermissionList: [], contentPermissionList: [], isEnabled: true, key: "admin", title: "Admin", isSuperAdmin: true, id: 1 }],
		serviceList,
		serviceLevelMap,
		settingsMap: null,
		contentTypeLevelMap: {},
		accessRole: roleKey
	};
};

export const getUserData = () => async (dispatch, getState) => {
	return new Promise((resolve) => {
		const pState = AccessManagementService.getState ? AccessManagementService.getState().catch(() => null) : Promise.resolve(null);
		const pRole = (AccessManagementService.getRole ? AccessManagementService.getRole() : Promise.resolve(null)).catch(() => null);
		const pUserGroupId = (AccessManagementService.getUserGroupId ? AccessManagementService.getUserGroupId() : Promise.resolve(null)).catch(() => null);

		Promise.all([pState, pRole, pUserGroupId]).then(([stateDto, _role, _userGroupId]) => {
			const client = extractClientFromState(stateDto);
			const userGroups = safeArr(stateDto?.userGroups ?? stateDto?.groups ?? client?.userGroups);
			const userGroupId = stateDto?.userGroupId ?? stateDto?.groupId ?? _userGroupId ?? null;

			const roles = safeArr(stateDto?.roles ?? client?.roles);
			const selectedRole = normalizeRole(stateDto?.role);
			const roleFromEndpoint = normalizeRole(_role);
			const nextRole = selectedRole || roleFromEndpoint || normalizeRole(roles?.[0]) || normalizeRole(client?.roles?.[0]) || 'operator';

			const prev = getState()?.auth?.user || {};
			const prevId = prev?.data?.id ?? null;
			const prevRole = Array.isArray(prev?.role) ? prev.role[0] : null;
			const nextId = client?.id ?? null;

			// ha ugyanaz a user+role+groupId, ne dispatch-elj újra (theme/layout loop ellen)
			if (prevId === nextId && String(prevRole || '') === String(nextRole || '') && (prev?.data?.userGroupId ?? null) === (userGroupId ?? null)) {
				resolve();
				return;
			}

			const usersSettings = JSON.parse(localStorage.getItem('userSettings'));
			const accessConfig = convertClientToAccessConfig({ client, userGroups, roles }, nextRole);

			dispatch(setUserData({
				role: [nextRole],
				accessRole: nextRole,
				serviceList: accessConfig.serviceList,
				data: { ...accessConfig.user, settings: usersSettings || {}, userGroupId: userGroupId ?? null, role: nextRole, userGroups, roles },
				roleList: accessConfig.roleList,
				serviceLevelMap: accessConfig.serviceLevelMap,
				userGroups,
				roles
			}));

			resolve();
		});
	});
};

export const setUserData = user => async (dispatch) => {
	if (!_.isUndefined(user.data.currentLocale) && user.data.currentLocale !== null) dispatch(changeLanguage(user.data.currentLocale));
	history.location.state = { redirectUrl: user.redirectUrl };
	dispatch(setDefaultSettings(user.data.settings));
	dispatch(setUser(user));
};

export const logoutUser = () => async (dispatch, getState) => {
	const { user } = getState().auth;
	if (!user.role || user.role.length === 0) return null;
	history.push({ pathname: '#' });
	AccessManagementService.logout();
	dispatch(setInitialSettings());
	return dispatch(userLoggedOut());
};

export const logoutUserSilent = () => async (dispatch) => {
	try { await Promise.resolve(AccessManagementService.logout()); } catch (e) { }
	dispatch(setInitialSettings());
	return dispatch(userLoggedOut());
};

export const updateUserSettings = settings => async (dispatch, getState) => {
	const oldUser = getState().auth.user;
	const user = _.merge({}, oldUser, { data: { settings } });
	dispatch(updateUserData(user));
	return dispatch(setUserData(user));
};

export const updateUserShortcuts = shortcuts => async (dispatch, getState) => { };

export const updateUserData = user => async () => {
	if (!user.role || user.role.length === 0) return;
	localStorage.setItem('userSettings', JSON.stringify(user.data.settings));
};

const initialState = { role: [], data: { name: 'Guest' } };

const userSlice = createSlice({
	name: 'auth/user',
	initialState,
	reducers: {
		setUser: (state, action) => action.payload,
		userLoggedOut: () => initialState
	},
	extraReducers: {}
});

export const { setUser, userLoggedOut } = userSlice.actions;

export default userSlice.reducer;
